// ============================================================================
// Upgrade Wizard Types
// ============================================================================

export type UpgradeComponentType = 'Website' | 'AdditionalUsers' | 'GoogleLocalServiceAds' | 'MetaAds';

export type BillingCycleOption = 'Annual' | 'Quarterly' | null;

// ============================================================================
// API Response Types
// ============================================================================

export interface OperationResult<T> {
	success: boolean;
	value?: T;
	error?: string;
}

export interface SubscriptionComponent {
	componentId: number;
	name: string;
	handle?: string;
	pricePointId?: number;
	allocatedQuantity: number;
}

export interface SubscriptionState {
	subscriptionId: number;
	state: string;
	productId: number;
	productName: string;
	productHandle: string;
	billingCycleMonths: number;
	billingCycleLabel: string;
	currentPeriodStartedAt: string;
	currentPeriodEndsAt: string;
	daysRemainingInPeriod: number;
	existingComponents: SubscriptionComponent[];
	isLegacyProduct: boolean;
	couponCodes?: string[];
	hasCoupons?: boolean;
	// Billing Cycle Change Eligibility
	isEligibleForBillingCycleChange: boolean;
	billingCycleChangeBlockers: string[];
	nextProductHandle?: string | null;
	nextProductId?: number | null;
}

export interface UpgradeComponentRequest {
	componentId: number;
	pricePointId: number;
	quantity: number;
}

export interface UpgradePreviewRequest {
	subscriptionId: number;
	accountId: string;
	components: UpgradeComponentRequest[];
	newBillingCycle: BillingCycleOption;
	useSplitBilling?: boolean;
	discountMonths?: 0 | 1 | 2 | 3;
}

export interface UpgradePreviewComponent {
	componentType: number;
	componentId: number;
	componentName?: string;
	quantity: number;
	unitPrice: number;
	proratedChargeInCents: number;
	proratedCharge: number;
	discountAmountInCents?: number;
	discountAmount?: number;
	netChargeInCents?: number;
	netCharge?: number;
	warning?: string;
}

export interface UpgradePreviewResponse {
	subscriptionId: number;
	currentState: SubscriptionState;
	components: UpgradePreviewComponent[];
	totalProratedChargeInCents: number;
	totalProratedCharge: number;
	totalDiscountInCents?: number;
	totalDiscount?: number;
	netTotalInCents?: number;
	netTotal?: number;
	totalTaxInCents: number;
	totalInCents: number;
	daysRemainingInPeriod: number;
	/** Total days in the current billing period (e.g., 89 for a quarterly cycle starting in February) */
	expectedDaysInPeriod?: number;
	// Immediate Billing Cycle Change Fields
	isImmediateBillingCycleChange: boolean;
	serviceCreditInCents: number;
	serviceCredit: number;
	newBillingCycleChargesInCents: number;
	newBillingCycleCharges: number;
	amountDueTodayInCents: number;
	amountDueToday: number;
	remainingCreditInCents: number;
	remainingCredit: number;
	scheduledProductChange?: string | null;
	// Split Billing Fields
	useSplitBilling?: boolean;
	splitBillingFirstPaymentInCents?: number;
	splitBillingFirstPayment?: number;
	splitBillingSecondPaymentInCents?: number;
	splitBillingSecondPayment?: number;
}

export interface UpgradeExecuteRequest {
	subscriptionId: number;
	accountId: string;
	components: UpgradeComponentRequest[];
	newBillingCycle: BillingCycleOption;
	useSplitBilling?: boolean;
	discountMonths?: 0 | 1 | 2 | 3;
}

export interface UpgradeExecuteComponentResult {
	componentType: number;
	componentId: number;
	componentName: string;
	allocationId: number;
	quantity: number;
	amountChargedInCents: number;
	amountCharged: number;
	success: boolean;
}

export interface SplitBillingResult {
	success: boolean;
	invoiceId: string;
	amountPaidInCents: number;
	amountPaid: number;
	amountDueInCents: number;
	amountDue: number;
	errorMessage: string | null;
}

export interface UpgradeExecuteResponse {
	success: boolean;
	subscriptionId: number;
	componentResults: UpgradeExecuteComponentResult[];
	totalChargedInCents: number;
	totalCharged: number;
	splitBillingResult?: SplitBillingResult | null;
	/** Non-fatal warnings from the upgrade (e.g., service credit could not be auto-applied) */
	warnings?: string[] | null;
	/** Surplus credit amount successfully applied to the next invoice */
	serviceCreditAppliedInCents?: number;
}

// ============================================================================
// Next Invoice Preview Types
// ============================================================================

export interface NextInvoiceLineItem {
	name: string;
	componentId: number | null;
	amountInCents: number;
	amount: number;
	discountAmountInCents: number;
	memo: string;
	isNewFromUpgrade: boolean;
}

export interface NextInvoicePreviewResponse {
	subscriptionId: number;
	currentState: SubscriptionState;
	nextInvoiceBillingCycleMonths: number;
	nextInvoiceBillingCycleLabel: string;
	nextInvoiceDate: string;
	lineItems: NextInvoiceLineItem[];
	subtotalInCents: number;
	subtotal: number;
	totalDiscountInCents: number;
	totalDiscount: number;
	totalTaxInCents: number;
	totalTax: number;
	estimatedTotalInCents: number;
	estimatedTotal: number;
	caveats: string[];
}

// ============================================================================
// Helper Functions
// ============================================================================

// ============================================================================
// Billing Cycle Change Types
// ============================================================================

export interface BillingCycleChangeRequest {
	accountId: string;
	newBillingCycle: 12 | 3;
}

export interface ComponentMapping {
	sourceHandle: string;
	targetHandle: string;
	componentName: string;
	shouldSkip: boolean;
	reason?: string;
}

export interface BillingCycleChangePreviewResult {
	subscriptionId: number;
	currentState: SubscriptionState;
	scheduledChange: string;
	targetBillingCycleLabel: string;
	targetBillingCycleMonths: number;
	targetProductHandle: string;
	componentMappings: ComponentMapping[];
}

export interface ComponentSwapResult {
	oldHandle: string;
	newHandle: string;
	quantity: number;
	success: boolean;
	errorMessage?: string;
}

export interface BillingCycleChangeResult {
	subscriptionId: number;
	success: boolean;
	scheduledProductChange?: string;
	componentSwapResults: ComponentSwapResult[];
	errorMessage?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determines which upgrade components are available based on existing subscription components.
 * Filters out components that the account already has (allocatedQuantity > 0).
 */
export function getAvailableUpgradeComponents(existingComponents: SubscriptionComponent[]): UpgradeComponentType[] {
	// Filter to only components that are actually allocated (quantity > 0)
	const allocatedComponents = existingComponents.filter(c => c.allocatedQuantity > 0);
	const allocatedNames = allocatedComponents.map(c => c.name.toLowerCase());

	const availableComponents: UpgradeComponentType[] = [];

	// Check for Website - hide if any allocated component contains "website"
	const hasWebsite = allocatedNames.some(name => name.includes('website'));
	if (!hasWebsite) {
		availableComponents.push('Website');
	}

	// Check for AdditionalUsers - hide if any allocated component contains "user"
	const hasUsers = allocatedNames.some(name => name.includes('user'));
	if (!hasUsers) {
		availableComponents.push('AdditionalUsers');
	}

	// Check for GoogleLocalServiceAds - hide if any allocated component contains "google" or "lsa" or "local service"
	const hasLSA = allocatedNames.some(
		name => name.includes('google') || name.includes('lsa') || name.includes('local service')
	);
	if (!hasLSA) {
		availableComponents.push('GoogleLocalServiceAds');
	}

	// Check for MetaAds - hide if any allocated component contains "facebook" or "meta" or "fb ads"
	const hasMetaAds = allocatedNames.some(
		name => name.includes('facebook') || name.includes('meta') || name.includes('fb ads')
	);
	if (!hasMetaAds) {
		availableComponents.push('MetaAds');
	}

	return availableComponents;
}

/**
 * Returns a user-friendly display name for a component type.
 */
export function getComponentDisplayName(componentType: UpgradeComponentType): string {
	switch (componentType) {
		case 'Website':
			return 'Website';
		case 'AdditionalUsers':
			return 'Additional Users';
		case 'GoogleLocalServiceAds':
			return 'Google LSA';
		case 'MetaAds':
			return 'Meta Ads';
		default:
			return componentType;
	}
}
